import React, { useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { HelpPageAri } from '@atlassian/ari/help';

import messages from '../messages';

import type { siteNavigation_AtlassianHubAppShell$key } from './__generated__/siteNavigation_AtlassianHubAppShell.graphql';
import type { HelpCenterFolder, HelpCenterPage, HelpCenterSiteTreeNode } from './types';
import { useSiteNavigationOverflow } from './use-site-navigation-overflow';

const EMPTY_SITE_NAVIGATION_ITEMS: HelpCenterSiteTreeNode[] = [];

const styles = cssMap({
	// Hug the packed folders so the filled nav row can centre the whole group.
	// Must lay out as a row to match the hidden measurement row it is packed against.
	centerSlot: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		minWidth: 0,
		position: 'relative',
	},
	folderLabel: {
		display: 'block',
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	// Mirrors the `subtle` appearance of @atlaskit/button so the packed folders
	// match the sibling nav triggers. The button cannot be used directly because
	// it drops the `style` prop that carries the hub's themed text colour.
	trigger: {
		display: 'inline-flex',
		boxSizing: 'border-box',
		alignItems: 'center',
		columnGap: token('space.050'),
		height: '2rem',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.medium'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	triggerSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	measurementRow: {
		position: 'absolute',
		top: 0,
		left: 0,
		display: 'flex',
		gap: token('space.050'),
		width: 'max-content',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

const FolderLabel = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
	<Box as="span" xcss={styles.folderLabel}>
		{children}
	</Box>
);

type NavTriggerProps = Omit<React.ComponentProps<typeof Pressable>, 'children' | 'xcss'> & {
	children: React.ReactNode;
	iconLabel: string;
	isSelected?: boolean;
	textColor?: string;
	truncate?: boolean;
};

const NavTrigger = React.forwardRef<HTMLButtonElement, NavTriggerProps>(function NavTrigger(
	{ children, iconLabel, isSelected = false, textColor, truncate = false, ...props },
	ref,
) {
	return (
		<Pressable
			{...props}
			ref={ref}
			style={{ color: isSelected ? undefined : textColor }}
			xcss={cx(styles.trigger, isSelected && styles.triggerSelected)}
		>
			{truncate ? <FolderLabel>{children}</FolderLabel> : children}
			<ChevronDownIcon color="currentColor" label={iconLabel} />
		</Pressable>
	);
});

const getFolderPages = (folder: HelpCenterFolder): HelpCenterPage[] =>
	folder.contents.nodes.filter(
		(node): node is HelpCenterPage => node.__typename === 'HelpCenterPage',
	);

const getPageId = (page: HelpCenterPage): string | null => {
	try {
		return HelpPageAri.parse(page.id).pageId;
	} catch {
		return null;
	}
};

type PageItemProps = {
	page: HelpCenterPage;
};

const PageItem = ({ page }: PageItemProps) => {
	const { push } = useRouterActions();
	const pageId = getPageId(page);

	if (!pageId) {
		return null;
	}

	return (
		<DropdownItem onClick={() => push(`/page/${pageId}`)}>{page.name?.default ?? ''}</DropdownItem>
	);
};

const FolderMenuItems = ({ pages }: { pages: HelpCenterPage[] }) => (
	<DropdownItemGroup>
		{pages.map((page) => (
			<PageItem key={page.id} page={page} />
		))}
	</DropdownItemGroup>
);

type FolderMenuProps = {
	folder: HelpCenterFolder;
	textColor?: string;
};

const PromotedFolderMenu = ({ folder, textColor }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="bottom-start"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<NavTrigger
					{...props}
					iconLabel={formatMessage(messages.downIcon)}
					ref={triggerRef}
					textColor={textColor}
					truncate
				>
					{folder.name?.default ?? ''}
				</NavTrigger>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowFolderMenu = ({ folder }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="right-start"
			shouldFlip
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<DropdownItem
					{...props}
					elemAfter={
						<ChevronRightIcon label={formatMessage(messages.openFolderSubmenu)} size="small" />
					}
					ref={triggerRef}
				>
					<FolderLabel>{folder.name?.default ?? ''}</FolderLabel>
				</DropdownItem>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowItem = ({ item }: { item: HelpCenterSiteTreeNode }) =>
	item.__typename === 'HelpCenterPage' ? (
		<PageItem page={item} />
	) : (
		<OverflowFolderMenu folder={item} />
	);

export const SiteNavigationMenus = ({
	overflowItems,
	promotedFolders,
	textColor,
}: {
	overflowItems: HelpCenterSiteTreeNode[];
	promotedFolders: HelpCenterFolder[];
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (promotedFolders.length === 0 && overflowItems.length === 0) {
		return null;
	}

	return (
		<>
			{promotedFolders.map((folder) => (
				<PromotedFolderMenu key={folder.id} folder={folder} textColor={textColor} />
			))}
			{overflowItems.length > 0 && (
				<DropdownMenu
					shouldRenderToParent
					trigger={({ triggerRef, ...props }) => (
						<NavTrigger
							{...props}
							iconLabel={formatMessage(messages.downIcon)}
							ref={triggerRef}
							testId="site-navigation-more"
							textColor={textColor}
						>
							{formatMessage(messages.more)}
						</NavTrigger>
					)}
				>
					<DropdownItemGroup>
						{overflowItems.map((item) => (
							<OverflowItem key={item.id} item={item} />
						))}
					</DropdownItemGroup>
				</DropdownMenu>
			)}
		</>
	);
};

export const SiteNavigation = ({
	queryRef,
	textColor,
}: {
	queryRef: siteNavigation_AtlassianHubAppShell$key | null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const slotRef = useRef<HTMLDivElement>(null);
	const measurementRowRef = useRef<HTMLDivElement>(null);
	const data = useFragment(
		graphql`
			fragment siteNavigation_AtlassianHubAppShell on HelpCenter {
				siteTree {
					root {
						contents {
							nodes {
								__typename
								... on HelpCenterPage {
									id
									name {
										default
									}
								}
								... on HelpCenterFolder {
									id
									name {
										default
									}
									contents {
										nodes {
											__typename
											... on HelpCenterPage {
												id
												name {
													default
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);
	const nodes = data?.siteTree?.root?.contents.nodes;
	const items = useMemo(
		() =>
			nodes?.filter(
				(node): node is HelpCenterSiteTreeNode =>
					node.__typename === 'HelpCenterPage' || node.__typename === 'HelpCenterFolder',
			) ?? EMPTY_SITE_NAVIGATION_ITEMS,
		[nodes],
	);
	const { overflowItems, promotedFolders } = useSiteNavigationOverflow({
		items,
		measurementRowRef,
		slotRef,
	});
	const folders = items.filter(
		(item): item is HelpCenterFolder => item.__typename === 'HelpCenterFolder',
	);

	return (
		<Box ref={slotRef} testId="nav-links-center-slot" xcss={styles.centerSlot}>
			<SiteNavigationMenus
				overflowItems={overflowItems}
				promotedFolders={promotedFolders}
				textColor={textColor}
			/>
			<Box
				aria-hidden="true"
				ref={measurementRowRef}
				testId="site-navigation-measurement-row"
				xcss={styles.measurementRow}
			>
				{folders.map((folder) => (
					<Box data-site-navigation-folder-id={folder.id} key={folder.id}>
						<NavTrigger iconLabel="" tabIndex={-1} textColor={textColor} truncate>
							{folder.name?.default}
						</NavTrigger>
					</Box>
				))}
				<Box data-site-navigation-more-item>
					<NavTrigger iconLabel="" tabIndex={-1} textColor={textColor}>
						{formatMessage(messages.more)}
					</NavTrigger>
				</Box>
			</Box>
		</Box>
	);
};
